import React, { useState, useContext, useMemo } from 'react';
import { Play, Loader2, Check } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { ResponsiveSheet } from '../ui/responsive-sheet';
import { Button } from '../ui/button';
import { resolveActionDevice, describeActionState } from '../../lib/sceneActions';
import { iconForCategory } from '../../lib/sceneCategories';

const SceneDetailsSheet = ({ scene, devices, open, onClose, onEdit }) => {
  const { activateScene } = useContext(AppContext);

  const [isRunning, setIsRunning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const SceneIcon = iconForCategory(scene.category);

  const handleActivate = async () => {
    if (isRunning) return;

    try {
      setIsRunning(true);
      await activateScene(scene.id);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1500);
    } catch (error) {
      console.log('Error activating scene:', error);
    } finally {
      setTimeout(() => setIsRunning(false), 2000);
    }
  };

  const devicesByLocation = useMemo(() => {
    if (!scene.actions) return {};

    const locationMap = {};

    scene.actions.forEach(action => {
      if (action.type === 'device') {
        // Resolve handles legacy parent-id actions, so they're no longer dropped.
        const device = resolveActionDevice(action, devices);
        if (device) {
          const location = device.location || 'Unknown Location';
          if (!locationMap[location]) {
            locationMap[location] = [];
          }
          locationMap[location].push({ ...device, action });
        }
      }
    });

    return locationMap;
  }, [scene.actions, devices]);

  return (
    <ResponsiveSheet open={open} onClose={onClose} size="lg">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-2xl bg-primary/8 border border-border/50 flex items-center justify-center shrink-0">
              <SceneIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{scene.name}</h2>
              <p className="text-sm text-muted-foreground">
                {scene.description || 'Scene'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
          >
            Done
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <Button
              onClick={handleActivate}
              disabled={isRunning}
              className="w-full h-14 rounded-2xl text-base"
            >
              {isRunning && !showSuccess ? (
                <>
                  <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                  Running...
                </>
              ) : showSuccess ? (
                <>
                  <Check className="mr-3 h-5 w-5" />
                  Scene Activated
                </>
              ) : (
                <>
                  <Play className="mr-3 h-5 w-5" />
                  Run Scene
                </>
              )}
            </Button>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Scene Category
                </h3>
                <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                  {scene.category || 'General'}
                </span>
              </div>

              <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                What this scene affects
              </h3>

              {Object.keys(devicesByLocation).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border/60 bg-card p-8 text-center">
                  <p className="text-sm text-muted-foreground">No devices configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(devicesByLocation).map(([location, locationDevices]) => (
                    <div
                      key={location}
                      className="rounded-2xl border border-border/60 bg-card overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-muted/30 border-b border-border/60">
                        <h4 className="text-sm font-semibold">{location}</h4>
                      </div>
                      <div className="divide-y">
                        {locationDevices.map((deviceWithAction) => (
                          <div key={deviceWithAction.id} className="px-4 py-3">
                            <p className="text-sm font-medium break-words">{deviceWithAction.name}</p>
                            <p className="text-xs text-muted-foreground mt-1 break-words">
                              {describeActionState(deviceWithAction.action, deviceWithAction)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              onEdit?.(scene);
            }}
            className="w-full h-11 rounded-xl"
          >
            Edit Scene
          </Button>
        </div>
    </ResponsiveSheet>
  );
};

export default SceneDetailsSheet;
